import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Filter } from "lucide-react";
import axios from "axios";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    linkedin: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/>
      </svg>
    ),
  };
  return icons[platform] || null;
};

const platformColors = { linkedin: "text-[#0A66C2]", twitter: "text-black", facebook: "text-[#1877F2]", instagram: "text-[#E4405F]" };
const platformNames = { linkedin: "LinkedIn", twitter: "X", facebook: "Facebook", instagram: "Instagram" };
const platforms = ["all", "linkedin", "twitter", "facebook", "instagram"];

export default function SocialPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  usePageSeo({
    title: "Social Media | LAB336 – Aktuelle Beiträge",
    description: "Aktuelle Social-Media-Beiträge von LAB336 zu Digitalisierung, KI und Prozessoptimierung. Automatisiert geteilt – weil wir praktizieren, was wir predigen.",
    canonical: `${BASE_URL}/social`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/social-feed?limit=50`)
      .then(res => setPosts(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? posts : posts.filter(p => p.platform === filter);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="social-page">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      <div className="bg-lab-bg-dark py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Social Media</span>
          <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white mt-4">
            Aktuelles aus unserem Netzwerk
          </h1>
          <p className="text-gray-400 mt-3 max-w-xl">
            Automatisiert geteilt – weil wir praktizieren, was wir predigen.
          </p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Filter */}
        <div className="flex items-center gap-2 mb-10 flex-wrap" data-testid="social-filter">
          <Filter className="w-4 h-4 text-lab-text-muted mr-1" />
          {platforms.map(p => (
            <button
              key={p}
              onClick={() => setFilter(p)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === p
                  ? "bg-lab-primary text-white"
                  : "bg-lab-bg-light text-lab-text-muted hover:bg-lab-bg-light/80 border border-lab-border"
              }`}
              data-testid={`filter-${p}`}
            >
              {p === "all" ? "Alle" : (
                <span className="flex items-center gap-1.5">
                  <PlatformIcon platform={p} className="w-3.5 h-3.5" />
                  {platformNames[p]}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-pulse text-lab-text-muted">Beiträge werden geladen...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lab-text-muted text-lg">Noch keine Beiträge vorhanden.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group bg-lab-bg-light border border-lab-border overflow-hidden hover:border-lab-accent/30 transition-colors"
                data-testid={`social-card-${post.id}`}
              >
                {post.image_url && (
                  <div className="aspect-square overflow-hidden bg-white">
                    <img
                      src={post.image_url}
                      alt={post.text?.substring(0, 80) || "Social Media Post"}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={platformColors[post.platform]}>
                        <PlatformIcon platform={post.platform} className="w-4 h-4" />
                      </span>
                      <span className="text-xs text-lab-text-muted font-medium">{platformNames[post.platform]}</span>
                    </div>
                    <span className="text-xs text-lab-text-muted">{formatDate(post.created_at)}</span>
                  </div>
                  {post.text && (
                    <p className="text-sm text-lab-text leading-relaxed mb-3">
                      {post.text}
                    </p>
                  )}
                  {post.post_url && (
                    <a
                      href={post.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-lab-accent hover:gap-2 transition-all"
                    >
                      Original ansehen <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
